import {
  Box,
  Divider,
  TableCell,
  TablePagination,
  Table,
  Alert,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { createUseStyles } from "react-jss";
import { Popin, Text, Thead } from "$components";
import JOrderTree from "$components/JOrderTree/JOrderTree";
import { MESSAGES } from "$components/EDB/IntegratedOrders";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import useEDBAvailableOrders from "$hooks/useEDBAvailableOrders";
import { Order, SuborderType } from "$types";
import { edbIoToOrders, ordersToEdbIo } from "./utils";
import Filters from "./Filters";
import Summary from "./Summary";
import { theme } from "$utils/theme/bataillog";

const DEFAULT_ITEMS_PER_PAGE = 20;
const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

type Props = {
  companyId: string;
  onValidate: (integratedOrders: EDBIntegratedOrders) => void;
  openPopin: boolean;
  setOpenPopin: (open: boolean) => void;
  dockyardId: string;
  initialIntegratedOrders?: EDBIntegratedOrders;
  isAddingMaterial: boolean;
};

function IntegratedOrdersPopin({
  companyId,
  onValidate,
  openPopin,
  setOpenPopin,
  dockyardId,
  initialIntegratedOrders,
  isAddingMaterial,
}: Props) {
  const classes = useStyles();
  const {
    availableOrders,
    filters,
    setFilter,
    resetFilters,
    QUANTITY_BADGE_WIDTH,
    pageIndex,
    setPageIndex,
    setItemsPerPage,
    itemsPerPage,
    itemsCount,
  } = useEDBAvailableOrders(
    companyId,
    dockyardId,
    // When adding material, don't exclude the initial edb IO.
    isAddingMaterial ? undefined : initialIntegratedOrders?.id,
    DEFAULT_ITEMS_PER_PAGE
  );

  const [tmpIntegratedOrders, setTmpIntegratedOrders] = useState(
    initialIntegratedOrders
  );

  useEffect(() => {
    setTmpIntegratedOrders(initialIntegratedOrders);
  }, [initialIntegratedOrders]);

  const handleOnCheck = (checkedProducts: Order[]) => {
    const newIntegratedOrders = ordersToEdbIo(checkedProducts);

    // Reuse previous integrated orders not displayed on the current page
    const filteredPrevIntegratedOrderProducts =
      tmpIntegratedOrders?.orderProducts?.filter((orderProduct) =>
        availableOrders.every(
          (availableOrder) => availableOrder.id !== orderProduct.order.id
        )
      ) || [];

    const mergedOrderProducts = [
      ...newIntegratedOrders.orderProducts,
      ...filteredPrevIntegratedOrderProducts,
    ];

    setTmpIntegratedOrders({
      ...tmpIntegratedOrders, // keep all meta data not present in the tree (the integratedOrders id for example)
      orderProducts: mergedOrderProducts,
    });
  };

  const handleOnCancel = () => {
    setOpenPopin(false);
    resetFilters();
    setTmpIntegratedOrders(initialIntegratedOrders);
  };

  // Recalculate filtered orders when availableOrders or dockyard filter changes
  const availableOrdersWithoutStockSubOrders = useMemo(() => {
    // Get dockyard filter if set
    const selectedDockyards = filters["suborders][dockyard"] || [];
    const dockyardFilterActive =
      Array.isArray(selectedDockyards) && selectedDockyards.length > 0;

    return availableOrders
      .map((order) => ({
        ...order,
        suborders: order.suborders.filter((suborder) => {
          // Always filter out stock suborders
          if (suborder.type === SuborderType.STOCK) {
            return false;
          }

          // If dockyard filter is active and suborder type is "dockyard",
          // only keep suborders that match the selected dockyards
          if (dockyardFilterActive && suborder.type === SuborderType.DOCKYARD) {
            return selectedDockyards.includes(suborder.dockyard);
          }

          // Keep all other suborders (non-stock, and non-dockyard when dockyard filter is active)
          return true;
        }),
      }))
      .filter((order) => order.suborders.length > 0);
  }, [availableOrders, filters]);

  return (
    <Popin
      openPopin={openPopin}
      toggleDrawer={setOpenPopin}
      minWidth={900}
      customCloseAction={handleOnCancel}
    >
      <Text type="h2">Intégrer des commandes</Text>
      {/* Top part: orders filtering & selection */}
      <div>
        <Box className={classes.filters}>
          <Filters
            companyId={companyId}
            filters={filters}
            resetFilters={resetFilters}
            setFilter={setFilter}
          />
        </Box>

        <Box className={classes.availableOrders}>
          {availableOrdersWithoutStockSubOrders.length > 0 ? (
            <>
              <Box display="flex">
                <Table>
                  <Thead style={{ width: "100%" }}>
                    {/* Fixed width to align with the three below */}
                    <TableCell width="510px">Commande</TableCell>
                    <TableCell
                      width={QUANTITY_BADGE_WIDTH}
                      style={{ paddingRight: 0 }}
                    >
                      Qté disponible <br />/ Qté commandée
                    </TableCell>
                    <TableCell style={{ paddingLeft: 0 }}>Unité</TableCell>
                  </Thead>
                </Table>
              </Box>
              <Box mt={1}>
                <JOrderTree
                  orders={availableOrdersWithoutStockSubOrders}
                  checkedOrders={edbIoToOrders(tmpIntegratedOrders)}
                  showCheckBox
                  onCheck={handleOnCheck}
                />
                <TablePagination
                  component="div"
                  count={itemsCount}
                  page={pageIndex}
                  onPageChange={(event, newPage) => setPageIndex(newPage)}
                  rowsPerPage={itemsPerPage}
                  onRowsPerPageChange={(event) => {
                    setItemsPerPage(parseInt(event.target.value, 10));
                    setPageIndex(0);
                  }}
                  rowsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Commandes par page :"
                />
              </Box>
            </>
          ) : (
            <Alert severity="info">{MESSAGES.NO_ORDERS_FOUND}</Alert>
          )}
        </Box>
      </div>

      <Divider className={classes.divider} />

      <Summary
        currentAvailableOrders={availableOrdersWithoutStockSubOrders}
        handleOnCancel={handleOnCancel}
        onValidate={onValidate}
        setTmpIntegratedOrders={setTmpIntegratedOrders}
        tmpIntegratedOrders={tmpIntegratedOrders}
      />
    </Popin>
  );
}

export default IntegratedOrdersPopin;

const useStyles = createUseStyles(() => ({
  availableOrders: {
    marginTop: theme.spacing(3),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0 !important`,
  },
  filters: {
    marginTop: theme.spacing(2),
  },
}));
